import type { AdminAppealStatus } from '@carcheck/shared';

/**
 * Appeal FSM source-state gates for the detail-drawer footer actions
 * (AUT-130, architect 🟠 #4 — the mockup shows the buttons always-on;
 * don't trust it).
 *
 * Mirrors `reportActions.ts` from the reports surface. Backend remains the
 * authority on the transition — these helpers are pure UX gating:
 *
 *   - "Начать рассмотрение" (review_start) is legal ONLY from `pending`
 *     (`AppealsService.startReview` transitions `pending → under_review`;
 *     any other source 409s via `InvalidAppealTransitionError`).
 *   - "Вынести решение" (resolve) is legal from `pending` OR `under_review`
 *     (`AppealsService.resolve` accepts both) — i.e. NOT from `resolved`.
 *   - A `resolved` appeal is terminal → the drawer renders read-only.
 *
 * Typed against `AdminAppealStatus` so renaming / removing a status in
 * `@carcheck/shared` surfaces here at typecheck rather than silently
 * shipping a stale gate.
 */
export const CAN_REVIEW_START_FROM: ReadonlySet<AdminAppealStatus> = new Set<AdminAppealStatus>([
  'pending',
]);

export const CAN_RESOLVE_FROM: ReadonlySet<AdminAppealStatus> = new Set<AdminAppealStatus>([
  'pending',
  'under_review',
]);

/** "Начать рассмотрение" — enabled only on `pending`. */
export const canReviewStart = (status: AdminAppealStatus): boolean =>
  CAN_REVIEW_START_FROM.has(status);

/** "Вынести решение" — enabled while not yet `resolved`. */
export const canResolve = (status: AdminAppealStatus): boolean => CAN_RESOLVE_FROM.has(status);

/** Terminal — the drawer surfaces the persisted decision read-only. */
export const isResolved = (status: AdminAppealStatus): boolean => status === 'resolved';
